import type { AccountingPostingDelivery, AccountingPostingRow } from './accountingApi';

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';

/**
 * THE STATE MACHINE THE LIST AND THE DETAIL DIALOG BOTH READ.
 *
 * `AccountingPostingRow.status` is only ever `QUEUED` or `REVERSED` — see
 * `accountingApi.ts`'s own header for why the third fact, whether a ledger
 * has actually taken the posting, lives on `deliveries[]` instead of a third
 * status value. A badge that only ever showed `posting.status` would collapse
 * "not yet attempted", "attempted and failed", "failed and exhausted every
 * retry", "in flight right now" and "accepted by the ledger" into one word,
 * `QUEUED` — which is exactly the "pending" catch-all this audit exists to
 * refuse. `DerivedStatus` is what a bookkeeper actually needs to tell apart:
 * a NOT_SENT posting needs a connector installed, a FAILED one needs a human,
 * a RETRYING one needs nothing yet, and POSTED needs nothing at all.
 *
 * A posting can fan out to more than one ACCOUNTING installation at once
 * (rare, but the schema allows a tenant-wide and a branch-scoped installation
 * to both cover the same branch). `PARTIAL` is the honest answer when they
 * disagree — reporting the whole posting as POSTED because *one* ledger took
 * it would hide the one that didn't.
 */
export type DerivedStatus =
  | 'REVERSED'
  | 'NOT_SENT'
  | 'FAILED'
  | 'RETRYING'
  | 'POSTING'
  | 'QUEUED'
  | 'POSTED'
  | 'PARTIAL'
  | 'CANCELLED';

export function deriveStatus(posting: Pick<AccountingPostingRow, 'status' | 'deliveries'>): DerivedStatus {
  if (posting.status === 'REVERSED') return 'REVERSED';

  const deliveries = posting.deliveries;
  if (deliveries.length === 0) return 'NOT_SENT';

  const has = (s: AccountingPostingDelivery['status']) => deliveries.some((d) => d.status === s);
  const all = (s: AccountingPostingDelivery['status']) => deliveries.every((d) => d.status === s);

  // Worst news first: an operator opening this screen is looking for what
  // needs them, the same ordering `fiscal.md` gives reconciliation's counts.
  if (has('DEAD_LETTERED')) return 'FAILED';
  if (has('FAILED')) return 'RETRYING';
  if (has('DELIVERING')) return 'POSTING';
  if (all('DELIVERED')) return 'POSTED';
  if (has('DELIVERED')) return 'PARTIAL';
  if (has('PENDING')) return 'QUEUED';
  return 'CANCELLED';
}

const DERIVED_STATUS_TONES: Record<DerivedStatus, Tone> = {
  // Undone, not wrong — the shared badge table's own reasoning for REVERSED.
  REVERSED: 'neutral',
  // Nothing will happen until a connector covers this branch. Not wrong by
  // itself — a venue may not have installed one yet — but it is the one state
  // that needs an operator's attention to ever change, so it is not neutral.
  NOT_SENT: 'warning',
  // Exhausted every retry the substrate allows. The loudest tone here,
  // deliberately, the same reasoning that makes REJECTED the loudest on the
  // fiscal screen.
  FAILED: 'danger',
  RETRYING: 'warning',
  POSTING: 'info',
  QUEUED: 'info',
  POSTED: 'success',
  PARTIAL: 'info',
  CANCELLED: 'neutral',
};

export function toneForDerivedStatus(status: DerivedStatus): Tone {
  return DERIVED_STATUS_TONES[status];
}

const KIND_TONES: Record<string, Tone> = {
  SALE: 'neutral',
  PAYOUT: 'neutral',
  REVERSAL: 'brand',
};

export function toneForKind(kind: string): Tone {
  return KIND_TONES[kind] ?? 'neutral';
}

const DELIVERY_STATUS_TONES: Record<string, Tone> = {
  PENDING: 'info',
  DELIVERING: 'info',
  DELIVERED: 'success',
  FAILED: 'warning',
  DEAD_LETTERED: 'danger',
  CANCELLED: 'neutral',
};

export function toneForDeliveryStatus(status: string): Tone {
  return DELIVERY_STATUS_TONES[status] ?? 'neutral';
}

/** mm:ss-free, locale-aware age of an ISO instant — "the oldest is from Tuesday" made generic. */
export function formatAge(iso: string, locale: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const ms = Date.now() - then;
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return locale.startsWith('ar') ? 'الآن' : 'just now';
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const rtf = new Intl.RelativeTimeFormat(locale.startsWith('ar') ? 'ar' : 'en', { numeric: 'auto' });
  if (days >= 1) return rtf.format(-days, 'day');
  if (hours >= 1) return rtf.format(-hours, 'hour');
  return rtf.format(-minutes, 'minute');
}

/** The reference line a row/detail shows for what this posting is actually about. */
export function referenceOf(posting: AccountingPostingRow): { label: string; kind: 'check' | 'shift' } {
  if (posting.payload.kind === 'SALE') return { label: posting.payload.checkNumber, kind: 'check' };
  if (posting.payload.kind === 'PAYOUT') return { label: posting.payload.shiftId.slice(-8), kind: 'shift' };
  // REVERSAL wraps whichever of the two it corrects.
  return posting.payload.original.kind === 'SALE'
    ? { label: posting.payload.original.checkNumber, kind: 'check' }
    : { label: posting.payload.original.shiftId.slice(-8), kind: 'shift' };
}
